// CANVAS-BLOCKS: the Live page's deep links (`/c/{slug}/live?view=…&session=…&tab=…`).
//   view     workspace | browser | computer (unset: Workspace, unless the link names a session,
//            a login or the Accounts tab: then the screens)
//   session  a screen lease (uuid); `lease` is the older alias and still works
//   tab      `accounts` opens the Accounts tab (CONNECTIONS-AREA, unchanged); any other value is
//            a browser tab id, passed through for the screen
// Agents post them in chat as markdown links; a message draws such a link as "Open in Live".

export type LiveView = "workspace" | "browser" | "computer";
export const LIVE_VIEWS: readonly LiveView[] = ["workspace", "browser", "computer"];

export const LEASE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A browser tab id: short, URL-safe. */
export const TAB_ID = /^[\w.:-]{1,80}$/;

export const isLiveView = (v: unknown): v is LiveView =>
  typeof v === "string" && (LIVE_VIEWS as readonly string[]).includes(v);

export type LiveLink = { slug: string; view: LiveView; session?: string; tab?: string };

/**
 * A Live deep link (relative, or absolute on this app's `origin`) as its parts; null for any
 * other link (another site, another page, a view that does not exist).
 */
export function parseLiveLink(href: string, origin: string): LiveLink | null {
  if (!href) return null;
  const absolute = /^[a-z][a-z\d+.-]*:/i.test(href);
  if (!absolute && !href.startsWith("/")) return null;
  let url: URL;
  let base: URL;
  try {
    base = new URL(origin);
    url = new URL(href, base);
  } catch {
    return null;
  }
  if (url.origin !== base.origin) return null;
  const m = /^\/c\/([^/]+)\/live\/?$/.exec(url.pathname);
  if (!m?.[1]) return null;
  let slug: string;
  try {
    slug = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  const q = url.searchParams;
  const rawView = q.get("view");
  if (rawView !== null && !isLiveView(rawView)) return null;
  const session = [q.get("session"), q.get("lease")].find(
    (s): s is string => !!s && LEASE_ID.test(s),
  );
  const tab = q.get("tab");
  const implied = session || tab === "accounts";
  const view: LiveView = rawView ?? (implied ? "browser" : "workspace");
  return {
    slug,
    view,
    ...(session ? { session } : {}),
    ...(tab && TAB_ID.test(tab) ? { tab } : {}),
  };
}

/** The in-app search of a Live link (what the route reads). */
export function liveLinkSearch(l: LiveLink): Record<string, string> {
  return {
    view: l.view,
    ...(l.session ? { session: l.session } : {}),
    ...(l.tab ? { tab: l.tab } : {}),
  };
}
